import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject
} from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { saveAs } from 'file-saver';
import { assign, first, isString } from 'lodash';
import { DateTime } from 'luxon';
import { concatMap, from, of } from 'rxjs';
import { LicenseForm } from 'src/app/classes/form-groups/license-form';
import { PrimaryTradeLicenseRequirementForm } from 'src/app/classes/form-groups/primary-trade-license-requirement-form';
import {
  PrimaryTradeLicenseRequirement,
  VendorTrade
} from 'src/app/classes/models';
import { License } from 'src/app/classes/models/license';
import { VendorTradeState } from 'src/app/classes/models/vendor-trade-state';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { LicenseService } from 'src/app/services/license.service';
import { ToastService } from 'src/app/services/toast.service';
import { TradeLicenseService } from 'src/app/services/trade-license.service';
import { UtilityService } from 'src/app/services/utility.service';

import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';

// Should refactor this to only retrieve license file when necessary
// Should refactor this to only retrieve license file when necessary
// Should refactor this to only retrieve license file when necessary
// Should refactor this to only retrieve license file when necessary
// Should refactor this to only retrieve license file when necessary
@Component({
  selector: 'app-license-upload-selection-modal',
  templateUrl: './license-upload-selection-modal.component.html',
  styleUrls: ['./license-upload-selection-modal.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class LicenseUploadSelectionModalComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);
  constructor(
    private ModalService: NgbModal,
    private ActiveModal: NgbActiveModal,
    private LicenseService: LicenseService,
    private TradeLicenseService: TradeLicenseService,
    private UtilityService: UtilityService,
    private ToastService: ToastService
  ) {}

  @ViewChild('fileUploadInput')
  fileUploadInputElement?: ElementRef<HTMLInputElement>;
  @ViewChild('fileChangeInput') fileChangeInput?: ElementRef<HTMLInputElement>;

  @Input() VendorId!: number;
  // Can optionally choose to pass in Licenses directly to this modal. If Licenses aren't passed in, the VendorId is
  // used to load Licenses from an endpoint for the Vendor.
  @Input() Licenses?: License[];
  // Pass in License requirements to allow license assignment.
  @Input()
  PrimaryTradeLicenseRequirementForms?: PrimaryTradeLicenseRequirementForm[];
  // The trade state the Vendor is currently assigning licenses for.
  @Input() VendorTradeState?: VendorTradeState;
  @Input() VendorTrade?: VendorTrade;
  @Input() PrimaryTradeLicenseRequirement?: PrimaryTradeLicenseRequirement;

  // Because this is a modal, we can't listen/handle for this event in HTML. However, we can
  // listen to the event by accessing the componentInstance of the modal. Could also create a service
  // for transmitting data between modal and instantiating component.
  @Output() archiveLicenseEvent: EventEmitter<License> =
    new EventEmitter<License>();

  // When the user selects the 'Assign License' button, this field is populated. When the user selects
  // a trade, this License is used for the assignment.
  SelectedLicense?: License;
  // This is passed to the display card column to handle the display of selected licenses.
  SelectedLicenseSet = new Set<License>();
  // Used for styling the drag and drop box when dragging file over.
  DragDropPanelStyle: GenericObject = {};
  // Used for determining how many child elements a file has been dragged over in the drop zone.
  // Increments on enter, decrements on leave. When dragCounter hits 0, drop zone class is removed.
  dragCounter: number = 0;
  // Allowed file types.
  AcceptableFileTypes = new Set(['image/png', 'application/pdf', 'image/jpeg']);
  // When assigning a license, we apply this style across all cards representing a trade
  // with a license requirement.
  LicenseAssignmentCardStyle: GenericObject = {
    border: '6px solid rgb(0, 135, 238)',
    cursor: 'pointer'
  };

  getTradeRequirementFormsWithoutLicenses() {
    return (
      this.PrimaryTradeLicenseRequirementForms?.filter((ptlrfg) => {
        const {
          value: { Licenses }
        } = ptlrfg;
        return !Licenses || Licenses.length === 0;
      }) || []
    );
  }

  // Checks if each license requirement has a license.
  areAllLicensesUploaded() {
    return (
      this.PrimaryTradeLicenseRequirementForms &&
      this.PrimaryTradeLicenseRequirementForms.every((ptlrfg) => {
        const {
          value: { Licenses }
        } = ptlrfg;
        return Licenses && Licenses.length > 0;
      })
    );
  }

  // Check if any licenses haven't been assigned to a license requirement.
  getAllUnassignedLicenses() {
    const UnassignedLicenses: License[] = [];
    if (this.Licenses) {
      this.Licenses.forEach((license) => {
        const { TradeLicenses } = license;
        if (TradeLicenses && TradeLicenses.length === 0) {
          UnassignedLicenses.push(license);
        }
      });
    }

    return UnassignedLicenses;
  }

  close() {
    const allLicensesUploaded = this.areAllLicensesUploaded();
    const allUnassignedLicenses = this.getAllUnassignedLicenses();

    if (!allLicensesUploaded || allUnassignedLicenses.length > 0) {
      const { componentInstance, result } = this.ModalService.open(
        ConfirmModalComponent,
        { backdrop: 'static', modalDialogClass: 'confirm-modal' }
      );
      const displayTextParts = [];

      if (!allLicensesUploaded) {
        displayTextParts.push(
          'A license image has not been uploaded for all trades.'
        );
      }

      if (allUnassignedLicenses.length > 0) {
        const unassignedLicenseFileNames = allUnassignedLicenses.map(
          (license) => license.licenseFileName
        );

        let unassignedLicenseFileNamesString: string = '';
        if (unassignedLicenseFileNames.length < 2) {
          unassignedLicenseFileNamesString =
            unassignedLicenseFileNames.pop() || '';
          unassignedLicenseFileNamesString =
            unassignedLicenseFileNamesString.concat(' was ');
        } else if (unassignedLicenseFileNames.length === 2) {
          unassignedLicenseFileNamesString =
            unassignedLicenseFileNames.join(' and ');
          unassignedLicenseFileNamesString =
            unassignedLicenseFileNamesString.concat(' were ');
        } else if (unassignedLicenseFileNames.length > 2) {
          const finalUnassignedLicenseFileName =
            unassignedLicenseFileNames.pop();
          unassignedLicenseFileNamesString =
            unassignedLicenseFileNames.join(', ');
          unassignedLicenseFileNamesString =
            unassignedLicenseFileNamesString.concat(
              ',',
              ' and ',
              finalUnassignedLicenseFileName!,
              ' were '
            );
        }

        displayTextParts.push(
          unassignedLicenseFileNamesString.concat('not assigned to a trade.')
        );
      }

      displayTextParts.push('Are you sure you want to exit this screen?');

      Object.assign(componentInstance, {
        displayText: displayTextParts.join('\n')
      });

      from(result).subscribe({
        next: (response) => {
          if (response) {
            this.ActiveModal.close();
          }
        }
      });
    } else {
      this.ActiveModal.close();
    }
  }

  selectLicense(License: License) {
    this.SelectedLicenseSet.clear();
    if (this.SelectedLicense !== License) {
      this.SelectedLicense = License;
      this.SelectedLicenseSet.add(this.SelectedLicense);
    } else {
      this.SelectedLicense = undefined;
    }
  }

  normalizeFileSizeText(fileSize: string | number): string {
    const fileSizeInteger = isString(fileSize)
      ? parseInt(fileSize, 10)
      : fileSize;
    if (fileSizeInteger >= 1000 && fileSizeInteger < 1000000) {
      return `${Math.round((fileSizeInteger / 1000) * 10) / 10} KB`;
    } else if (fileSizeInteger >= 1000000) {
      return `${Math.round((fileSizeInteger / 1000000) * 10) / 10} MB`;
    } else {
      return `${fileSizeInteger} Bytes`;
    }
  }

  generateFileDataUri({
    fileType,
    fileData
  }: {
    fileType: string;
    fileData: string;
  }): string {
    // If the file data is already a base64 encoded uri, return the uri.
    if (/data:[\w/]*;base64,/.test(fileData)) {
      return fileData;
    }
    return `data:${fileType};base64,${fileData}`;
  }

  downloadFile(License: License) {
    const { licenseFileName, licenseFile, LicenseFile } = License;

    if (!licenseFileName) {
      throw this.ToastService.addErrorToast(
        'Error',
        'License file is missing file name.',
        { returnError: true }
      );
    }

    if (LicenseFile) {
      this.LicenseService.getLicenseFileS3PresignedUrl(
        this.VendorId,
        License.id!
      ).subscribe({
        next: (s3Response) => {
          saveAs(s3Response.presignedUrl, licenseFileName);
        }
      });
    } else if (licenseFileName && licenseFile) {
      const dataURI = this.generateFileDataUri({
        fileType: licenseFileName.split('.').pop()!,
        fileData: licenseFile
      });
      saveAs(dataURI, licenseFileName);
    }
  }

  isLicenseExpired(License: License) {
    const { expirationDate } = License;

    if (expirationDate) {
      const monthsFromExpiration = DateTime.fromJSDate(new Date()).diff(
        DateTime.fromJSDate(expirationDate),
        'months'
      ).months;
      return monthsFromExpiration >= 0;
    }

    return false;
  }

  // Handle a file selected from the file selection window
  selectedFile(File: Event) {
    if (!this.fileUploadInputElement) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing File upload input element.',
        { returnError: true }
      );
    }
    const { nativeElement } = this.fileUploadInputElement;
    const { files } = nativeElement;
    if (files) {
      if (files.length > 1) {
        this.ToastService.addErrorToast(
          'Error',
          'Cannot upload more than one file at once.'
        );
        throw new Error('Cannot upload more than one file at once.');
      }
      const file = first(files);
      if (file) {
        this.fileUpload({ data: file });
      }
    }
    nativeElement.value = '';
  }

  // This triggers the file selection window to pop up.
  uploadFile() {
    if (this.fileUploadInputElement) {
      this.fileUploadInputElement.nativeElement.click();
    }
  }

  fileDragEnter(event: Event) {
    this.DragDropPanelStyle = {
      border: '2px solid rgb(0, 135, 238)',
      height: 'calc-size(auto, size + 10px)',
      display: 'flex',
      'flex-direction': 'column',
      'justify-content': 'center'
    };
    this.dragCounter += 1;
  }

  fileDragLeave(event: Event) {
    this.dragCounter -= 1;
    if (this.dragCounter === 0) {
      this.DragDropPanelStyle = {};
    }
  }

  fileDragOver(event: Event) {
    event.preventDefault();
  }

  fileUpload({ data: File }: { data: File }) {
    this.DragDropPanelStyle = {};
    this.dragCounter = 0;
    const { name: fileName, size: fileSize, type: fileType } = File;

    if (!this.AcceptableFileTypes.has(fileType)) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Accepted file types are: JPEG, PNG, & PDF.',
        { returnError: true }
      );
    }

    from(File.arrayBuffer()).subscribe({
      next: (arrayBuffer) => {
        if (!arrayBuffer) {
          throw new Error('Could not upload file. Error processing file data.');
        }

        const base64EncodedFileData =
          this.UtilityService.convertArrayBufferToBase64EncodedString(
            arrayBuffer
          );
        const base64EncodedFileDataUri = `data:${fileType};base64,${base64EncodedFileData}`;

        const License = {
          VendorId: this.VendorId,
          licenseFile: base64EncodedFileDataUri,
          licenseFileName: fileName,
          licenseState:
            this.VendorTradeState?.SubNationalDivision?.abbreviation ||
            this.PrimaryTradeLicenseRequirement!.SubNationalDivision
              ?.abbreviation
        } as License;

        this.LicenseService.saveLicense(this.VendorId, License).subscribe({
          next: (License: License) => {
            if (this.Licenses) {
              this.Licenses.push(License);
            } else {
              this.Licenses = [License];
            }

            if (
              this.PrimaryTradeLicenseRequirementForms?.length === 1 &&
              this.Licenses.length === 1
            ) {
              this.SelectedLicense = this.Licenses[0];
              this.assignLicense(
                this.PrimaryTradeLicenseRequirementForms[0]
              ).subscribe({
                next: () => {
                  this.SelectedLicense = undefined;
                  this._cdr.markForCheck();
                }
              });
            }

            // To do. Can display toast for successful License upload.
            this._cdr.markForCheck();
          }
        });
      }
    });
  }

  // This event should be overridden if extended into a new component.
  // Used to handle a file drop event.
  fileDrop(dropEvent: DragEvent) {
    dropEvent.preventDefault();

    if (dropEvent.dataTransfer) {
      const files = dropEvent.dataTransfer.files;
      if (files.length > 1) {
        this.ToastService.addErrorToast(
          'Error',
          'Cannot upload more than one file at once.'
        );
        throw new Error('Cannot upload more than one file at once.');
      }
      const file = first(files);
      if (file) {
        this.fileUpload({ data: file });
      }
    }
  }

  viewData(License: License, index: number) {
    const { licenseFileName, licenseFile, LicenseFile } = License;

    if (!licenseFileName) {
      throw this.ToastService.addErrorToast(
        'Error',
        'License is missing file name.',
        { returnError: true }
      );
    }

    if (LicenseFile) {
      this.LicenseService.getLicenseFileS3PresignedUrl(
        this.VendorId,
        License.id!
      ).subscribe({
        next: (s3Response) => {
          window.open(s3Response.presignedUrl, '_blank');
        }
      });
    } else if (licenseFileName && licenseFile) {
      const licenseFileParts = licenseFile.split(',');

      const licenseFileTypePart = licenseFileParts[0]
        .split(':')[1]
        .split(';')[0];
      const licenseFilePart = licenseFileParts[1];

      const ArrayBuffer =
        this.UtilityService.convertBase64StringToArrayBuffer(licenseFilePart);
      window.open(
        URL.createObjectURL(
          new Blob([ArrayBuffer], { type: licenseFileTypePart })
        ),
        '_blank'
      );
    }
  }

  assignLicense(
    PrimaryTradeLicenseRequirementFormGroup: PrimaryTradeLicenseRequirementForm
  ) {
    if (!this.SelectedLicense) {
      throw this.ToastService.addErrorToast(
        'Error',
        'You must select a license to assign it to a trade.',
        { returnError: true }
      );
    }

    const { id: LicenseId } = this.SelectedLicense;

    const {
      controls: {
        id: { value: PrimaryTradeLicenseRequirementId },
        VendorTrade: VendorTradeControl,
        Licenses: LicensesControl
      }
    } = PrimaryTradeLicenseRequirementFormGroup;

    if (!VendorTradeControl) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error assigning this License. Vendor trade information is missing.',
        { returnError: true }
      );
    }

    if (!LicensesControl) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error assigning this License. License information is missing.',
        { returnError: true }
      );
    }

    const {
      value: { id: VendorTradeId }
    } = VendorTradeControl;

    if (!PrimaryTradeLicenseRequirementId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error assigning this License. Trade requirement is missing id.',
        { returnError: true }
      );
    }

    if (!LicenseId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error assigning this License. The License is missing an id.',
        { returnError: true }
      );
    }

    if (!VendorTradeId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error assigning this License. The Vendor trade is missing an id.',
        { returnError: true }
      );
    }

    // isNotArchived is set on the backend.
    const TradeLicense = {
      LicenseId,
      VendorTradeId,
      PrimaryTradeLicenseRequirementId
    };

    return this.TradeLicenseService.saveTradeLicense(
      this.VendorId,
      TradeLicense
    ).pipe(
      concatMap((SavedTradeLicense) => {
        if (!this.SelectedLicense!.TradeLicenses) {
          this.SelectedLicense!.TradeLicenses = [];
        }

        const { TradeLicenses } = this.SelectedLicense!;
        TradeLicenses!.push(SavedTradeLicense);
        const LicenseFormGroup = new LicenseForm();
        LicenseFormGroup.patchValue(this.SelectedLicense!);
        LicensesControl!.push(LicenseFormGroup);

        if (this.getTradeRequirementFormsWithoutLicenses().length === 0) {
          this.SelectedLicense = undefined;
          this.SelectedLicenseSet.clear();
        }

        this._cdr.markForCheck();
        return of(SavedTradeLicense);
      })
    );
  }

  unassignLicense(
    PrimaryTradeLicenseRequirementFormGroup: PrimaryTradeLicenseRequirementForm
  ) {
    const confirmModal = this.ModalService.open(ConfirmModalComponent, {
      backdrop: 'static',
      modalDialogClass: 'confirm-modal'
    });
    assign(confirmModal.componentInstance, {
      displayText:
        'Are you sure you want to remove the license image from this trade?'
    });

    const {
      controls: {
        id: { value: PrimaryTradeLicenseRequirementId },
        VendorTrade: VendorTradeControl,
        Licenses: LicensesControl
      }
    } = PrimaryTradeLicenseRequirementFormGroup;

    if (!PrimaryTradeLicenseRequirementId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error removing the license. License requirement is missing id.',
        { returnError: true }
      );
    }

    if (!VendorTradeControl) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error removing the license. Missing Vendor trade information.',
        { returnError: true }
      );
    }

    const {
      value: { id: VendorTradeId }
    } = VendorTradeControl;

    if (!LicensesControl || LicensesControl.length === 0) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error removing the license. Missing license information.',
        { returnError: true }
      );
    }

    if (LicensesControl.length !== 1) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error removing the license. Multiple licenses belong to this trade.',
        { returnError: true }
      );
    }

    const License = LicensesControl.value[0];

    const { id: LicenseId } = License;

    if (!LicenseId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error removing the license. Missing license id.',
        { returnError: true }
      );
    }

    confirmModal.result.then((confirm) => {
      if (confirm) {
        this.TradeLicenseService.deleteTradeLicense(this.VendorId, {
          VendorTradeId,
          LicenseId,
          PrimaryTradeLicenseRequirementId
        }).subscribe({
          next: () => {
            LicensesControl.clear();
            this._cdr.markForCheck();
          }
        });
      }
    });
  }

  archiveLicense(License: License, index: number) {
    const confirmModal = this.ModalService.open(ConfirmModalComponent, {
      backdrop: 'static',
      modalDialogClass: 'confirm-modal'
    });
    assign(confirmModal.componentInstance, {
      displayText:
        'Are you sure you want to delete this uploaded license image?'
    });

    const { id: LicenseId } = License;

    confirmModal.result.then((confirm) => {
      if (confirm) {
        // This will archive the License (and the associated TradeLicense if assigned)
        this.LicenseService.saveLicense(
          this.VendorId,
          assign(License, { status: 'Archived' })
        ).subscribe({
          next: (ArchivedLicense: License) => {
            this.PrimaryTradeLicenseRequirementForms?.forEach(
              (TradeLicenseRequirementForm) => {
                const {
                  controls: { Licenses: LicenseControls }
                } = TradeLicenseRequirementForm;
                if (LicenseControls && LicenseControls.length > 0) {
                  // Should only be one license control per requirement form.
                  const {
                    value: { id }
                  } = LicenseControls.at(0);

                  if (id === LicenseId) {
                    LicenseControls.removeAt(0);
                  }
                }
              }
            );

            // Can display success toast.
            if (License === this.SelectedLicense) {
              this.SelectedLicense = undefined;
              this.SelectedLicenseSet.clear();
            }

            if (this.Licenses) {
              this.Licenses.splice(index, 1);
              this.archiveLicenseEvent.emit(ArchivedLicense);
            }
            this._cdr.markForCheck();
          }
        });
      }
    });
  }

  changeImage() {
    if (this.fileChangeInput) {
      this.fileChangeInput.nativeElement.click();
    }
  }

  changeFile(File: Event, License: License) {
    if (!this.fileChangeInput) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing File upload input element.',
        { returnError: true }
      );
    }

    if (!License) {
      throw this.ToastService.addErrorToast('Error', 'Missing lice');
    }

    const { nativeElement } = this.fileChangeInput;
    const { files } = nativeElement;

    if (files) {
      if (files.length > 1) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Cannot upload more than one file at once.',
          { returnError: true }
        );
      }

      const file = first(files);

      if (file) {
        const { name: fileName, size: fileSize, type: fileType } = file;

        if (!this.AcceptableFileTypes.has(fileType)) {
          this.ToastService.addErrorToast(
            'Error',
            'Accepted file types are: JPEG, PNG, and PDF.'
          );
          nativeElement.value = '';
          return;
        }

        from(file.arrayBuffer()).subscribe({
          next: (arrayBuffer) => {
            if (!arrayBuffer) {
              throw new Error(
                'Could not upload file. Error processing file data.'
              );
            }

            const base64EncodedFileData =
              this.UtilityService.convertArrayBufferToBase64EncodedString(
                arrayBuffer
              );
            const base64EncodedFileDataUri = `data:${fileType};base64,${base64EncodedFileData}`;

            this.LicenseService.saveLicense(
              this.VendorId,
              Object.assign(License, {
                VendorId: this.VendorId,
                licenseFile: base64EncodedFileDataUri,
                licenseFileName: fileName,
                licenseState:
                  this.VendorTradeState?.SubNationalDivision?.abbreviation ||
                  this.PrimaryTradeLicenseRequirement!.SubNationalDivision
                    ?.abbreviation
              })
            ).subscribe({
              next: (License: License) => {
                const index = this.Licenses!.findIndex(
                  (l) => l.id === License.id
                );
                if (index !== -1) {
                  this.Licenses![index] = License;
                }

                const LicensesControl =
                  this.PrimaryTradeLicenseRequirementForms![0].controls
                    .Licenses;
                LicensesControl?.patchValue([License]);
                LicensesControl?.updateValueAndValidity();
                this._cdr.markForCheck();
              }
            });
          }
        });
      }
    }

    nativeElement.value = '';
  }

  ngOnInit(): void {
    if (!this.VendorId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
        returnError: true
      });
    }

    if (!this.PrimaryTradeLicenseRequirementForms) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing License requirements.',
        { returnError: true }
      );
    }

    if (!this.VendorTradeState && !this.VendorTrade) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Vendor Trade State information.',
        { returnError: true }
      );
    }

    if (
      !this.PrimaryTradeLicenseRequirementForms.every(
        (pt) => pt.controls['PrimaryTrade']
      )
    ) {
      throw this.ToastService.addErrorToast(
        'Error',
        'License requirement is missing trade information.',
        { returnError: true }
      );
    }

    if (
      !this.PrimaryTradeLicenseRequirementForms.every(
        (pt) => pt.controls['VendorTrade']
      )
    ) {
      throw this.ToastService.addErrorToast(
        'Error',
        'License requirement is missing Vendor trade information.',
        { returnError: true }
      );
    }

    const { SubNationalDivision } =
      this.VendorTradeState! || this.PrimaryTradeLicenseRequirement!;

    if (!SubNationalDivision) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Vendor Trade State is missing Sub National division information.',
        { returnError: true }
      );
    }

    const { abbreviation } = SubNationalDivision;

    this.LicenseService.getLicenses(this.VendorId, {
      licenseState: abbreviation,
      TradeLicense: JSON.stringify({
        options: { required: false }
      }),
      LicenseFile: JSON.stringify({
        options: { required: false }
      })
    }).subscribe({
      next: (Licenses: License[]) => {
        this.Licenses = Licenses;
        this._cdr.markForCheck();
      }
    });
  }
}
